import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import { useParticipant } from "../contexts/ParticipantContext";
import {
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from "../design/fieldset";
import { Input } from "../design/input";
import { Participant } from "../models/Participant";

interface Context {
  openDialog: (onCreated?: (participantId: Participant["_id"]) => void) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

interface Errors {
  firstName?: string;
  lastName?: string;
}

const CreateParticipantDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const CreateParticipantDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createParticipant } = useParticipant();

  const [isOpen, setIsOpen] = useState(false);

  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [onCreatedCallback, setOnCreatedCallback] = useState<
    ((participantId: Participant["_id"]) => void) | undefined
  >(undefined);

  const openDialog = (
    onCreated?: (participantId: Participant["_id"]) => void,
  ) => {
    setOnCreatedCallback(() => onCreated);
    setName("");
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setName("");
    setOnCreatedCallback(undefined);
  };

  const handleYes = async () => {
    try {
      const participantId = await createParticipant(null, {
        firstName,
        lastName,
        name: `${firstName} ${lastName}`,
      });
      if (onCreatedCallback) {
        onCreatedCallback(participantId);
      }
      closeDialog();
    } catch (error) {
      console.error("Error completing participantId:", error);
      alert("Failed to complete participant. Please try again later.");
    }
  };

  return (
    <CreateParticipantDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>Create Participant</DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
                <Field>
                  <Label>First Name</Label>
                  <Input
                    type="text"
                    value={firstName}
                    placeholder="First Name"
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  {errors.firstName && (
                    <ErrorMessage>{errors.firstName}</ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Last Name</Label>
                  <Input
                    type="text"
                    value={lastName}
                    placeholder="Last Name"
                    onChange={(e) => setLastName(e.target.value)}
                  />
                  {errors.lastName && (
                    <ErrorMessage>{errors.lastName}</ErrorMessage>
                  )}
                </Field>
              </div>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={closeDialog}>
            Cancel
          </Button>
          <Button onClick={handleYes}>Save</Button>
        </DialogActions>
      </Dialog>
      {children}
    </CreateParticipantDialogProviderContext.Provider>
  );
};

export const useCreateParticipantDialog = () => {
  const ctx = useContext(CreateParticipantDialogProviderContext);
  if (!ctx)
    throw new Error(
      "useCreateParticipantDialog must be used inside CreateParticipantDialogProvider",
    );
  return ctx;
};
